/**
 * liveScoreState.js — pure helpers for the server-held live match state.
 *
 * Every live score write replaces matches.score_data, so the scorer sends the
 * whole state each time (LS-B07 / LS-B08):
 *
 *   {
 *     participant1, participant2,   // current totals (set sports: points in the current set)
 *     gameScores: [{ participant1, participant2 }],   // completed sets / games
 *     gamesWon:   { participant1, participant2 },
 *     currentSet, matchWon,         // set sports
 *     period, periodLabel,          // current period (quarter, half, set …)
 *     periods: [{ label, participant1, participant2 }] // line score
 *   }
 *
 * Boards keep their sets as { p1, p2 } — the helpers convert in both directions.
 */

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const parseObject = (value) => {
  if (!value) return null;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === "object" ? parsed : null;
    } catch {
      return null;
    }
  }
  return typeof value === "object" ? value : null;
};

/** score_data of a match response (camelCase or snake_case, string or object). */
export const getMatchScoreData = (match) =>
  parseObject(match?.scoreData) || parseObject(match?.score_data) || null;

/** A unique id for one scoring action, used for idempotent retries and undo. */
export const newClientEventId = (prefix = "evt") => {
  const rand =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  return `${prefix}_${rand}`.slice(0, 100);
};

/** [{p1,p2} | {participant1,participant2}] → [{participant1, participant2}] */
export const toServerGameScores = (list) =>
  (Array.isArray(list) ? list : [])
    .filter((g) => g && typeof g === "object")
    .map((g) => ({
      participant1: num(g.participant1 ?? g.p1),
      participant2: num(g.participant2 ?? g.p2),
    }));

/** [{participant1,participant2} | {p1,p2}] → [{p1, p2}] (board shape) */
export const toBoardGameScores = (list) =>
  toServerGameScores(list).map((g) => ({ p1: g.participant1, p2: g.participant2 }));

const countWins = (gameScores) =>
  gameScores.reduce(
    (acc, g) => {
      if (g.p1 > g.p2) acc.p1 += 1;
      else if (g.p2 > g.p1) acc.p2 += 1;
      return acc;
    },
    { p1: 0, p2: 0 }
  );

/**
 * Board game state ({ gamesWon, gameScores, currentGame, matchWon }) stored in
 * a score_data, or null when the score carries no set/game state.
 */
export const gameStateFromScoreData = (scoreData) => {
  const sd = parseObject(scoreData);
  if (!sd || !Array.isArray(sd.gameScores)) return null;
  const gameScores = toBoardGameScores(sd.gameScores);
  const won = sd.gamesWon && typeof sd.gamesWon === "object"
    ? { p1: num(sd.gamesWon.participant1 ?? sd.gamesWon.p1), p2: num(sd.gamesWon.participant2 ?? sd.gamesWon.p2) }
    : countWins(gameScores);
  const currentGame = Number.isInteger(Number(sd.currentSet)) && Number(sd.currentSet) > 0
    ? Number(sd.currentSet)
    : gameScores.length + 1;
  const state = {
    gamesWon: won,
    gameScores,
    currentGame,
    matchWon: sd.matchWon === true,
  };
  if (sd.matchFirstServer === "p1" || sd.matchFirstServer === "p2") {
    state.matchFirstServer = sd.matchFirstServer;
  }
  return state;
};

/** Board game state → the score_data fields that carry it. */
export const gameStateToScoreFields = (gameState) => {
  if (!gameState || !Array.isArray(gameState.gameScores)) return {};
  const fields = {
    gameScores: toServerGameScores(gameState.gameScores),
    gamesWon: {
      participant1: num(gameState.gamesWon?.p1 ?? gameState.gamesWon?.participant1),
      participant2: num(gameState.gamesWon?.p2 ?? gameState.gamesWon?.participant2),
    },
    currentSet: Number(gameState.currentGame) > 0 ? Number(gameState.currentGame) : gameState.gameScores.length + 1,
    matchWon: gameState.matchWon === true,
  };
  if (gameState.matchFirstServer === "p1" || gameState.matchFirstServer === "p2") {
    fields.matchFirstServer = gameState.matchFirstServer;
  }
  return fields;
};

/** Line score for a set sport: one row per completed set plus the set in play. */
export const setSportPeriods = (gameState, score, unit = "Set") => {
  const done = toServerGameScores(gameState?.gameScores);
  const rows = done.map((g, i) => ({ label: `${unit} ${i + 1}`, ...g }));
  if (!gameState?.matchWon) {
    rows.push({
      label: `${unit} ${done.length + 1}`,
      participant1: num(score?.participant1),
      participant2: num(score?.participant2),
    });
  }
  return rows;
};

/**
 * Add `delta` points for `side` to the line-score row of `period` (timed
 * sports). Missing rows are created; a row never goes below 0.
 */
export const addToPeriodLines = (periods, { period, label, side, delta }) => {
  const p = Number(period) > 0 ? Number(period) : 1;
  const rows = (Array.isArray(periods) ? periods : []).map((r) => ({ ...r }));
  while (rows.length < p) {
    rows.push({ label: `P${rows.length + 1}`, participant1: 0, participant2: 0 });
  }
  const row = rows[p - 1];
  if (label) row.label = label;
  if (side === "participant1" || side === "participant2") {
    row[side] = Math.max(0, num(row[side]) + num(delta));
  }
  return rows;
};

/** Normalise a stored periods[] array. */
export const normalizePeriods = (periods) =>
  (Array.isArray(periods) ? periods : [])
    .filter((r) => r && typeof r === "object")
    .map((r, i) => ({
      label: typeof r.label === "string" && r.label ? r.label : `P${i + 1}`,
      participant1: num(r.participant1 ?? r.p1),
      participant2: num(r.participant2 ?? r.p2),
    }));

/** Current period (number) and its label stored in a score_data, or null. */
export const periodFromScoreData = (scoreData) => {
  const sd = parseObject(scoreData);
  const period = Number(sd?.period);
  if (!Number.isInteger(period) || period < 1) return null;
  return { period, periodLabel: typeof sd.periodLabel === "string" ? sd.periodLabel : null };
};

/**
 * The full score_data to send with a live write.
 *  score        { participant1, participant2 }
 *  gameState    board game state (set sports) or null
 *  periodInfo   { period, periodLabel } or null
 *  periods      line score rows (timed sports) or null
 *  periodUnit   "Set" | "Game" — label of a set-sport row
 */
export const buildLiveScoreData = ({ score, gameState = null, periodInfo = null, periods = null, periodUnit = "Set" }) => {
  const data = {
    participant1: num(score?.participant1),
    participant2: num(score?.participant2),
  };
  if (gameState && Array.isArray(gameState.gameScores)) {
    Object.assign(data, gameStateToScoreFields(gameState));
    data.period = data.currentSet;
    data.periodLabel = `${periodUnit} ${data.currentSet}`;
    data.periods = setSportPeriods(gameState, score, periodUnit);
    return data;
  }
  if (periodInfo?.period) {
    data.period = Number(periodInfo.period);
    if (periodInfo.periodLabel) data.periodLabel = periodInfo.periodLabel;
  }
  if (Array.isArray(periods)) data.periods = normalizePeriods(periods);
  return data;
};

// ─── Server clock (LS-B09) ──────────────────────────────────────────────────

/**
 * Normalise a clock state from the server ({ period, periodLabel, elapsedMs,
 * running, runningSince, updatedAt }, camelCase or snake_case), or null.
 */
export const parseClockState = (raw) => {
  const c = parseObject(raw);
  if (!c) return null;
  const elapsed = Number(c.elapsedMs ?? c.elapsed_ms);
  if (!Number.isFinite(elapsed)) return null;
  const since = c.runningSince ?? c.running_since ?? null;
  const running = (c.running === true) && !!since && Number.isFinite(Date.parse(since));
  return {
    period: Number(c.period) > 0 ? Number(c.period) : 1,
    periodLabel: c.periodLabel ?? c.period_label ?? null,
    elapsedMs: Math.max(0, elapsed),
    running,
    runningSince: running ? since : null,
    updatedAt: c.updatedAt ?? c.updated_at ?? null,
  };
};

/** Clock state stored on a match response, or null. */
export const getMatchClockState = (match) =>
  parseClockState(match?.clockState ?? match?.clock_state);

/** Milliseconds on the clock right now: elapsedMs + time since runningSince. */
export const clockElapsedMs = (clockState, now = Date.now()) => {
  const c = parseClockState(clockState);
  if (!c) return 0;
  if (!c.running) return c.elapsedMs;
  return c.elapsedMs + Math.max(0, now - Date.parse(c.runningSince));
};

// ─── Event feed helpers (LS-B12 and follow-up b) ─────────────────────────────

/** Every id an event can be recognised by: feed id, server eventId, clientEventId. */
export const eventKeys = (event) => {
  const data = event?.eventData || event?.event_data || event?.data || {};
  return [event?.id, event?.eventId, event?.clientEventId, data?.clientEventId]
    .filter((k) => k !== undefined && k !== null && k !== "")
    .map(String);
};

/**
 * Merge incoming feed events into the existing log, newest first, dropping
 * any whose id / eventId / clientEventId is already known (never type+time).
 */
export const mergeFeedEvents = (existing, incoming, limit = 50) => {
  const seen = new Set();
  (existing || []).forEach((e) => eventKeys(e).forEach((k) => seen.add(k)));
  const fresh = [];
  (incoming || []).forEach((e) => {
    const keys = eventKeys(e);
    if (keys.length > 0 && keys.some((k) => seen.has(k))) return;
    keys.forEach((k) => seen.add(k));
    fresh.push(e);
  });
  if (fresh.length === 0) return existing;
  return [...fresh, ...(existing || [])].slice(0, limit);
};

/**
 * Points an event put on the board: an explicit points / scorePoints value,
 * otherwise 1 for a scoring action. Returns 0 for non-scoring events.
 */
export const eventPoints = (event) => {
  const data = event?.eventData || event?.event_data || {};
  const explicit = Number(event?.points ?? event?.scorePoints ?? data.points ?? data.scorePoints);
  const scores = event?.affectsScore ?? data.affectsScore;
  if (scores === false) return 0;
  if (Number.isFinite(explicit) && explicit > 0) return explicit;
  return scores ? 1 : 0;
};

const FEED_EXCLUDED = new Set(["lineup_update", "game_state_update", "clock_update"]);

/**
 * A feed entry for one stored match_events row (GET /websocket/matches/:id/events),
 * or null for rows the feed does not show.
 */
export const historyRowToFeedEvent = (row) => {
  if (!row) return null;
  const type = row.event_type || row.eventType;
  if (!type || FEED_EXCLUDED.has(type)) return null;
  if (row.voided) return null;
  const data = parseObject(row.event_data) || parseObject(row.eventData) || {};
  if (data.undo) return null;
  const created = row.created_at || row.timestamp || null;
  const time = created ? new Date(created) : null;
  const id = row.eventId || (row.id != null ? `evt_${row.id}` : null);
  const base = {
    id: id || data.clientEventId || `${type}_${created}`,
    eventId: row.eventId || null,
    clientEventId: data.clientEventId || null,
    type: type === "score_update" ? ((Number(data.points) || 0) < 0 ? "score_decrease" : "score_increase") : type,
    participant: data.participantKey || data.participant || data.side || "system",
    participantName: data.participantName || null,
    description: data.enhancedDescription || data.description || `${type.replace(/_/g, " ")} event`,
    hideParticipantName: true,
    period: row.period_number ?? data.period ?? null,
    timestamp: time && !Number.isNaN(time.getTime()) ? time.toLocaleTimeString() : "",
    fromHistory: true,
  };
  if (type === "score_update") {
    const after = parseObject(row.score_after) || data.scoreData || null;
    const side = base.participant;
    if (after && (side === "participant1" || side === "participant2")) base.score = num(after[side]);
    if (Number(data.points) > 0) base.points = Number(data.points);
  }
  if (type === "advanced_score") {
    Object.assign(base, {
      playerName: data.playerName,
      playerUserId: data.playerUserId || null,
      metricKey: data.metricKey,
      metricLabel: data.metricLabel,
      affectsScore: data.affectsScore,
      isPositive: data.isPositive,
      scorePoints: data.scorePoints,
    });
  }
  return base;
};
